/**
 * period-date.ts — „Data okresu" dokumentu (miesiąc księgowy w Symfonii FK).
 *
 * Wartość to zawsze pierwszy dzień miesiąca (`YYYY-MM-01`) albo `null`. Formatowanie
 * etykiety miesiąca żyje w generycznym `lib/month-label.ts`.
 */

import { isFkSafeDateYear } from '@saas/shared';
import type { TFunction } from 'i18next';

import { extractValidationIssues, getFieldError } from '@/lib/errors';

/** Klucz pola w payloadzie PATCH faktury i w `details` odpowiedzi VALIDATION_ERROR. */
export const PERIOD_DATE_FIELD = 'symfoniaPeriodDate';

/**
 * Toast dla błędu walidacji, który dotyczy WYŁĄCZNIE Daty okresu: „Data okresu: <komunikat
 * serwera>". Komunikat serwera jest konkretny („musi być 1. dniem miesiąca", „rok poza zakresem
 * Symfonii FK"), a ogólny opis z issue Zoda mówiłby tylko „zły format". `null`, gdy błąd dotyczy
 * też innych pól albo serwer nie przysłał komunikatu — wtedy strona używa zwykłego toastu.
 */
export function periodDateValidationToast(err: unknown, t: TFunction): string | null {
  const issues = extractValidationIssues(err);
  if (issues.length === 0 || issues.some((issue) => issue.path[0] !== PERIOD_DATE_FIELD)) {
    return null;
  }
  const message = getFieldError(err, PERIOD_DATE_FIELD);
  if (!message) return null;
  return t('common.validationIssue.toastTemplate', {
    field: t('tenant.documents.decree.editor.periodDateLabel'),
    reason: message,
  });
}

/**
 * `YYYY-MM-DD` → `YYYY-MM-01` (pierwszy dzień miesiąca) albo `null` dla pustej/złej daty
 * ORAZ dla rocznika spoza zakresu obsługiwanego przez Symfonię FK (np. literówka `0026-09-15`):
 * serwer odrzuciłby taki okres, więc zamiast podsuwać go (i blokować zapis faktury) — brak
 * podpowiedzi.
 */
export function monthStartOf(date: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(date ?? '');
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  if (!isFkSafeDateYear(`${m[1]}-${m[2]}-01`)) return null;
  return `${m[1]}-${m[2]}-01`;
}
